import {
  Icon24LogoBeebleColorLarge,
  Icon24LogoBriaColorLarge,
  Icon24LogoBytedanceColorLarge,
  Icon24LogoCassetteAiLarge,
  Icon24LogoCivitaiColorLarge,
  Icon24LogoElevenlabsLarge,
  Icon24LogoEnhancorLarge,
  Icon24LogoFalColorLarge,
  Icon24LogoFluxLarge,
  Icon24LogoGenmoColorLarge,
  Icon24LogoGoogleColorLarge,
  Icon24LogoGoogleGeminiLarge,
  Icon24LogoHaiperLarge,
  Icon24LogoHappyhorseLarge,
  Icon24LogoHeygenColorLarge,
  Icon24LogoHiggsfieldColorLarge,
  Icon24LogoIdeogramLarge,
  Icon24LogoKlingColorLarge,
  Icon24LogoKreaLarge,
  Icon24LogoLtxLarge,
  Icon24LogoLumaColorLarge,
  Icon24LogoMagnificLarge,
  Icon24LogoMeshyLarge,
  Icon24LogoMetaColorLarge,
  Icon24LogoMinimaxColorLarge,
  Icon24LogoMoonvalleyColorLarge,
  Icon24LogoNetflixColorLarge,
  Icon24LogoNvidiaColorLarge,
  Icon24LogoOpenAiLarge,
  Icon24LogoPixelcutColorLarge,
  Icon24LogoPixverseColorLarge,
  Icon24LogoRecraftLarge,
  Icon24LogoReplicateLarge,
  Icon24LogoReveLarge,
  Icon24LogoRodinLarge,
  Icon24LogoRunwayLarge,
  Icon24LogoSkyreelColorLarge,
  Icon24LogoStabilityAiColorLarge,
  Icon24LogoSyncLarge,
  Icon24LogoTencentColorLarge,
  Icon24LogoTopazLarge,
  Icon24LogoTripoLarge,
  Icon24LogoVectorizerLarge,
  Icon24LogoVeedColorLarge,
  Icon24LogoWanColorLarge,
  Icon24LogoWeavyLarge,
  Icon24LogoWorldlabsLarge,
  Icon24LogoXaiLarge,
} from '@figma/fpl-icons';
import {
  TextIteratorIcon,
  LevelsIcon,
  SlidersIcon,
  EyeIcon,
  PromptIcon,
  ImportIcon,
  ExportIcon,
  PainterIcon,
  ColorPaletteIcon,
  CompositorIcon,
  CropIcon,
  ResizeIcon,
  BlurIcon,
  ChannelsIcon,
  InvertIcon,
  CustomEffectIcon,
  SparkleIcon,
  PromptEnhanceIcon,
  PromptConcatIcon,
  AudioDescriberIcon,
  ImageDescriberIcon,
  NumberIcon,
  ToggleIcon,
  ListIcon,
  SeedIcon,
  MasksIcon,
  TextIcon,
  StickyNoteIcon,
  OutputIcon,
  RouterIcon,
  MergeAlphaIcon,
  ExtractVideoFrameIcon,
  RelightIcon,
  VideoSmooterIcon,
  VideoUpscaleIcon,
  ThreeDIcon,
  MaskExpandIcon,
  VideoDescriberIcon,
  VideoMatteIcon,
  ImageIteratorIcon,
  VideoIteratorIcon,
  HorizontalArrowsIcon,
  Rotate90ClockwiseIcon,
  PointCloudIcon,
  DepthIcon,
  BlendIcon,
  FigmaDesignLogo,
  GifIcon,
  KeyframeIcon,
  TrimIcon,
  VideoConcatenatorIcon,
  VideoCameraIcon,
} from '@/UI/Icons';
import { Hi3dIcon } from '@/components/Nodes/utils/Hi3dIcon';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { SVGIcon } from '@figma/fpl-icons/support';

/** Purple from the existing Gemini menu mark. The FPL sparkle is single-color, so this keeps it colored. */
const GEMINI_LOGO_COLOR = '#8679cd';

const providerLogos: Record<string, SVGIcon> = {
  pixelcut: Icon24LogoPixelcutColorLarge,
  alibaba: Icon24LogoHappyhorseLarge,
  fal: Icon24LogoFalColorLarge,
  replicate: Icon24LogoReplicateLarge,
  civit: Icon24LogoCivitaiColorLarge,
  weavy: Icon24LogoWeavyLarge,
  pixverse: Icon24LogoPixverseColorLarge,
  gemini: Icon24LogoGoogleGeminiLarge,
  wan: Icon24LogoWanColorLarge,
  ltx100x100: Icon24LogoLtxLarge,
  minimax: Icon24LogoMinimaxColorLarge,
  meshy: Icon24LogoMeshyLarge,
  nvidia: Icon24LogoNvidiaColorLarge,
  openai: Icon24LogoOpenAiLarge,
  vectorizer: Icon24LogoVectorizerLarge,
  recraft: Icon24LogoRecraftLarge,
  rodin: Icon24LogoRodinLarge,
  runway: Icon24LogoRunwayLarge,
  stability_ai: Icon24LogoStabilityAiColorLarge,
  skyreels: Icon24LogoSkyreelColorLarge,
  tencent: Icon24LogoTencentColorLarge,
  topaz: Icon24LogoTopazLarge,
  bria: Icon24LogoBriaColorLarge,
  black_forest: Icon24LogoFluxLarge,
  genmo100x100: Icon24LogoGenmoColorLarge,
  google: Icon24LogoGoogleColorLarge,
  haiper: Icon24LogoHaiperLarge,
  ideogram: Icon24LogoIdeogramLarge,
  kling: Icon24LogoKlingColorLarge,
  krea: Icon24LogoKreaLarge,
  luma: Icon24LogoLumaColorLarge,
  magnific: Icon24LogoMagnificLarge,
  bytedance: Icon24LogoBytedanceColorLarge,
  cassetteai: Icon24LogoCassetteAiLarge,
  eleven_labs: Icon24LogoElevenlabsLarge,
  moonvalley: Icon24LogoMoonvalleyColorLarge,
  higgsfield: Icon24LogoHiggsfieldColorLarge,
  reve: Icon24LogoReveLarge,
  sync: Icon24LogoSyncLarge,
  enhancor: Icon24LogoEnhancorLarge,
  ltx: Icon24LogoLtxLarge,
  meta: Icon24LogoMetaColorLarge,
  grok: Icon24LogoXaiLarge,
  beeble: Icon24LogoBeebleColorLarge,
  netflix: Icon24LogoNetflixColorLarge,
  tripo: Icon24LogoTripoLarge,
  veed: Icon24LogoVeedColorLarge,
  heygen: Icon24LogoHeygenColorLarge,
  worldlab: Icon24LogoWorldlabsLarge,
};

function providerLogo(key: string) {
  const Icon = providerLogos[key];
  if (!Icon) {
    return undefined;
  }
  if (key === 'gemini') {
    return <Icon aria-hidden style={{ '--fpl-icon-color': GEMINI_LOGO_COLOR } as CSSProperties} />;
  }
  return <Icon aria-hidden />;
}

const modelIconMap: Record<string, string | ReactElement> = {
  hitem3d: <Hi3dIcon />,
  relight: <RelightIcon />,
  video_smoother: <VideoSmooterIcon />,
  real_esrgan: <VideoUpscaleIcon />,
  trellis: <ThreeDIcon />,
  prompt: <PromptIcon />,
  import: <ImportIcon />,
  camera: <VideoCameraIcon />,
  export: <ExportIcon />,
  preview: <EyeIcon />,
  importmodel: <ImportIcon />,
  painter: <PainterIcon />,
  color_palette: <ColorPaletteIcon />,
  MultiLoRA: <ImportIcon />,
  crop: <CropIcon />,
  resize: <ResizeIcon />,
  trim: <TrimIcon />,
  compv3: <CompositorIcon />,
  video_concatenator: <VideoConcatenatorIcon />,
  comparison: <HorizontalArrowsIcon />,
  blend: <BlendIcon />,
  blur: <BlurIcon />,
  channels: <ChannelsIcon />,
  mergealpha: <MergeAlphaIcon />,
  invert: <InvertIcon />,
  video_frame: <ExtractVideoFrameIcon />,
  any_llm: <SparkleIcon />,
  prompt_enhance: <PromptEnhanceIcon />,
  'Prompt Enhancer': <PromptEnhanceIcon />,
  // Palette row id from menu.json; `icon`/`name` on that row is often any_llm or a provider PNG.
  '7gKmskdJQ28nMlxdB6aR': <PromptEnhanceIcon />,
  prompt_concat: <PromptConcatIcon />,
  audio_decribe: <AudioDescriberIcon />,
  image_decribe: <ImageDescriberIcon />,
  'Image Describer': <ImageDescriberIcon />,
  yLY6gjes6Ca2cKBTtAn5: <ImageDescriberIcon />,
  video_decribe: <VideoDescriberIcon />,
  router: <RouterIcon />,
  output: <OutputIcon />,
  stickynote: <StickyNoteIcon />,
  number: <NumberIcon />,
  string: <TextIcon />,
  boolean: <ToggleIcon />,
  seed: <SeedIcon />,
  array: <ListIcon />,
  mux: <ListIcon />,
  import_model: <ImportIcon />,
  import_lora: <ImportIcon />,
  multi_lora: <ImportIcon />,
  compositor: <CompositorIcon />,
  masks: <MasksIcon />,
  bria_masks: <MasksIcon />,
  merge_alpha: <MergeAlphaIcon />,
  extract_video_frame: <ExtractVideoFrameIcon />,
  sticky_note: <StickyNoteIcon />,
  levels: <LevelsIcon />,
  color_correction: <SlidersIcon />,
  custom_glsl: <CustomEffectIcon />,
  'text-iterator': <TextIteratorIcon />,
  'image-iterator': <ImageIteratorIcon />,
  'video-iterator': <VideoIteratorIcon />,
  dilation_erosion: <MaskExpandIcon />,
  video_matte: <VideoMatteIcon />,
  'point-cloud-from-depth': <PointCloudIcon />,
  rotate: <Rotate90ClockwiseIcon />,
  depth: <DepthIcon />,
  figma: <FigmaDesignLogo />,
  'fofr/toolkit': <GifIcon />,
  keyframe: <KeyframeIcon />,
  // Palette row id from 20260709_wea-5183-add-trim-node.sql; `icon` on that row is often `weavy`.
  'd98b1df5-b31a-4191-8446-34967ec3fe75': <TrimIcon />,
};

/** Prefer a node-specific glyph (id / display name / icon) over a shared model name like `any_llm`. */
export function resolveMenuItemIcon(item: {
  icon?: string | null;
  name?: string | null;
  id?: string | null;
  displayName?: string | null;
}): string | ReactNode {
  for (const key of [item.id, item.displayName, item.icon, item.name]) {
    if (!key) {
      continue;
    }
    const logo = providerLogo(key);
    if (logo) {
      return logo;
    }
    const mapped = modelIconMap[key];
    if (mapped != null && typeof mapped !== 'string') {
      return mapped;
    }
  }
  return item.icon ? modelIconMap[item.icon] : undefined;
}

export default modelIconMap;
